import { useState } from 'react';

const Pricing = () => {
  const [selected, setSelected] = useState<string | null>(null);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const plans = [
    {
      id: 'monthly',
      badge: 'MOST POPULAR',
      name: 'Monthly Plan',
      setupFee: '£150',
      price: '£13.99',
      period: '/month',
      setupNote: 'Activation & onboarding fee',
      savingsNote: 'Best value over 12 months',
      color: 'orange',
      features: [
        '24/7 Emergency Callout',
        'Annual CCTV Drain Survey',
        'Priority Response Time',
        'No Callout Charges',
        'Parts & Labour Included',
        'Drain Jetting Service',
        'Online Account Portal',
      ],
      cta: 'Start Monthly Plan',
    },
    {
      id: 'allinone',
      badge: null,
      name: 'All-In-One Monthly',
      setupFee: null,
      price: '£29.99',
      period: '/month',
      setupNote: 'No setup fee required',
      savingsNote: 'Simple, all-inclusive pricing',
      color: 'dark',
      features: [
        '24/7 Emergency Callout',
        'Annual CCTV Drain Survey',
        'Priority Response Time',
        'No Callout Charges',
        'Parts & Labour Included',
        'Drain Jetting Service',
        'Online Account Portal',
        'Cancel Anytime',
      ],
      cta: 'Start All-In-One Plan',
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Flexible Payment Options</span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-5">
            Choose Your Protection Plan
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Two simple options — both giving you complete drainage protection. Pick what works best for your budget.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-8 flex flex-col transition-all cursor-pointer ${
                selected === plan.id
                  ? 'ring-2 ring-orange-500 scale-[1.02]'
                  : 'border border-gray-100 hover:border-orange-200'
              } ${plan.color === 'orange' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
              onClick={() => setSelected(plan.id)}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full whitespace-nowrap">
                  {plan.badge}
                </div>
              )}

              <h3 className={`text-xl font-bold mb-2 ${plan.color === 'orange' ? 'text-white' : 'text-gray-900'}`}>
                {plan.name}
              </h3>

              {/* Setup fee */}
              {plan.setupFee && (
                <div className="bg-orange-500/20 border border-orange-500/30 rounded-xl px-4 py-3 mb-4 flex items-center gap-3">
                  <i className="ri-information-line text-orange-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <span className="text-orange-300 font-bold text-sm">{plan.setupFee}</span>
                    <span className="text-orange-300/70 text-sm"> {plan.setupNote}</span>
                    <div className="text-orange-300/50 text-xs mt-0.5">Covers first inspection, system registration &amp; account setup</div>
                  </div>
                </div>
              )}
              {!plan.setupFee && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 mb-4 flex items-center gap-3">
                  <i className="ri-check-double-line text-green-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-green-400 font-semibold text-sm">{plan.setupNote}</span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-end gap-1 mb-1">
                <span className={`text-6xl font-extrabold leading-none ${plan.color === 'orange' ? 'text-white' : 'text-gray-900'}`}>
                  {plan.price}
                </span>
                <span className={`text-lg mb-2 ${plan.color === 'orange' ? 'text-gray-400' : 'text-gray-400'}`}>
                  {plan.period}
                </span>
              </div>
              <p className={`text-sm mb-6 ${plan.color === 'orange' ? 'text-gray-400' : 'text-gray-500'}`}>
                {plan.savingsNote}
              </p>

              {/* Divider */}
              <div className={`h-px mb-6 ${plan.color === 'orange' ? 'bg-white/10' : 'bg-gray-100'}`}></div>

              {/* Features */}
              <ul className="flex flex-col gap-3 flex-1 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center bg-orange-500/20 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-orange-400 text-xs"></i>
                    </div>
                    <span className={plan.color === 'orange' ? 'text-gray-300' : 'text-gray-600'}>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={(e) => { e.stopPropagation(); scrollToContact(); }}
                className={`w-full py-4 rounded-full font-bold text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  plan.color === 'orange'
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Comparison note */}
        <div className="text-center mt-10">
          <p className="text-gray-400 text-sm">
            <i className="ri-calculator-line mr-1"></i>
            Monthly Plan: £150 activation + £13.99/mo &nbsp;|&nbsp; All-In-One: £29.99/mo with no setup fee
          </p>
          <p className="text-gray-400 text-xs mt-2">All plans include VAT. Cancel anytime with 30 days notice. Claim limits apply.</p>
          <p className="text-gray-400 text-xs mt-1">
            This is a service agreement, not an insurance policy. Cover is subject to exclusions and limits.{' '}
            <a href="/terms" className="text-orange-400 hover:underline cursor-pointer">View full Terms &amp; Conditions</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
